import { useMemo } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import type { DashboardSetupItem } from "@/shared/onboarding/types/dashboard-analytics.types";
import { useHasEverPublished } from "@/shared/services/useHasEverPublished";
import { resolveHasPaymentWay } from "@/shared/lib/store-readiness";
import type {
  SellerPermission,
  StoreCapability,
  SellerAuthView,
} from "@/lib/api/types";

export type DashboardSetupGuide = {
  coreOnly: boolean;
  items: DashboardSetupItem[];
  completed: number;
  total: number;
  /** 0..100, integer. */
  percent: number;
  /**
   * Both sources have answered. Until they have, every item reads as NOT done —
   * the guide is built from absent data — so anything that decides what to show
   * from `items` must wait for this or it will show the unfinished state first
   * and correct itself a moment later.
   */
  ready: boolean;
};

/**
 * Computes the store-setup checklist entirely from data the app shell already
 * has (session overview + the store record).
 *
 * There used to be a ninth row, "turn on the help bubble", whose `done` read the
 * `a2z:page-help:v1:<slug>` localStorage flag — a per-browser READING PREFERENCE,
 * default off. A merchant who finished every real step and declined the bubble
 * sat at 8/9 = 89% forever, and the card auto-hides at 100%, so it never hid.
 * The checklist is about the STORE; a preference is not a setup step, so the row
 * is gone and a finished store now actually reaches 100%.
 *
 * No extra network cost, same approach as `useSellerTasks`. Items with no backing in the current payload
 * (custom domain, theme colours/fonts) are deliberately omitted rather than
 * guessed. Deep-links point at where each step is completed.
 */
/**
 * The signals every checklist row is decided from. Deliberately ALL store facts
 * — nothing here is a browser preference or a UI toggle.
 *
 * That is the rule the ninth row broke: "turn on the page helper" read a
 * localStorage flag, so a merchant who declined the bubble could never finish
 * setup and the card never auto-hid. Keeping the decision in one pure function
 * makes "is this a store fact?" a question someone has to answer in review.
 */
export type SetupSignals = {
  hasCategories: boolean;
  hasProducts: boolean;
  hasPaymentWay: boolean;
  hasLogo: boolean;
  hasDescription: boolean;
  hasContact: boolean;
  /** Covers BOTH the "look" and "publish" rows — one journey, one signal. */
  hasPublished: boolean;
};

/**
 * Where the `look` row goes: Store Settings → **Design**, not the editor.
 *
 * The two rows used to share `/store/editor`, which was right while the theme
 * gallery WAS a drawer in the editor. Rule 23 moved it to its own settings
 * page, so "what does my shop look like" and "let buyers see it" now have two
 * different doors — and a row that lands a merchant in the editor with no
 * gallery in it is a row that answers nothing. Publish stays in the editor,
 * which is the only place it happens.
 *
 * Module scope so the test can pin them; the hook only prefixes the locale and
 * the store slug.
 */
export const SETUP_LOOK_PATH = "/settings?section=design";
export const SETUP_PUBLISH_PATH = "/store/editor";

/**
 * Row ids in display order.
 *
 * PRODUCTS first, categories second — the order was the other way round, which
 * put "add categories" in front of "add a product" and had the launcher name
 * categorising an empty catalogue as the merchant's next action. A shop with
 * one product and no sections is a shop; a shop with five sections and nothing
 * in them is not. The order matters only here: `completed`, `percent` and the
 * auto-hide at 100% all count rows rather than read them in sequence, so a
 * merchant who has finished setup sees no change.
 */
export const SETUP_ITEM_IDS = [
  "products",
  "categories",
  "payment",
  "logo",
  "description",
  "contact",
  "look",
  "publish",
] as const;

export type SetupItemId = (typeof SETUP_ITEM_IDS)[number];

// The floating helper offers work this actor can perform, without converting
// inaccessible or unknown store facts into completed checklist rows.
const SETUP_ACTION_PERMISSIONS: Record<
  SetupItemId,
  readonly SellerPermission[]
> = {
  products: ["PRODUCT_VIEW", "PRODUCT_CREATE"],
  categories: ["CATEGORY_VIEW", "CATEGORY_CREATE"],
  payment: ["STORE_VIEW", "PAYMENT_VIEW", "PAYMENT_EDIT"],
  logo: ["STORE_VIEW", "STORE_EDIT"],
  description: ["STORE_VIEW", "STORE_EDIT"],
  contact: ["STORE_VIEW", "STORE_EDIT"],
  look: ["DESIGN_VIEW", "DESIGN_EDIT"],
  publish: ["DESIGN_VIEW", "DESIGN_PUBLISH"],
};

const SETUP_CAPABILITIES: Record<SetupItemId, StoreCapability> = {
  products: "products",
  categories: "categories",
  payment: "storeSettings",
  logo: "logo",
  description: "profileBranding",
  contact: "profileBranding",
  look: "design",
  publish: "design",
};

export function actionableSetupIds(
  seller: SellerAuthView | undefined,
): SetupItemId[] {
  if (seller?.storeAccess !== true) return [];
  return SETUP_ITEM_IDS.filter(
    (id) =>
      (seller.access?.capabilities.profileBranding === true ||
        id === "products") &&
      seller.access?.capabilities[SETUP_CAPABILITIES[id]] === true &&
      (id === "payment" && !seller.access?.capabilities.manualPayments
        ? (["STORE_VIEW", "STORE_EDIT"] as SellerPermission[])
        : SETUP_ACTION_PERMISSIONS[id]
      ).every((permission) => seller.permissions.includes(permission)),
  );
}

/**
 * Whether the store has a way to get paid at all. COD counts — same rule as
 * `useSellerTasks` — which is the part worth testing: `resolveSetupDone` only
 * passes the answer through, so a test that hands it `hasPaymentWay: true`
 * asserts nothing.
 *
 * The rule itself now lives in `shared/lib/store-readiness.ts`, because the
 * share sheet's readiness note asks the same question and a second copy would
 * eventually answer it differently. Re-exported here so this module stays the
 * checklist's single entry point.
 */
export { resolveHasPaymentWay };

/** Which rows are done, from store facts alone. Pure, so the rule is testable. */
export function resolveSetupDone(
  signals: SetupSignals,
): Record<SetupItemId, boolean> {
  return {
    products: signals.hasProducts,
    categories: signals.hasCategories,
    payment: signals.hasPaymentWay,
    logo: signals.hasLogo,
    description: signals.hasDescription,
    contact: signals.hasContact,
    look: signals.hasPublished,
    publish: signals.hasPublished,
  };
}

/** 0..100, integer. 100 exactly when every row is done. */
export function setupPercent(done: readonly boolean[]): number {
  if (done.length === 0) {
    return 0;
  }
  return Math.round((done.filter(Boolean).length / done.length) * 100);
}

export function useDashboardSetupGuide({
  storeSlug,
  locale,
}: {
  storeSlug: string;
  locale: string;
}): DashboardSetupGuide {
  const t = useTranslations();
  const { data: sessionData } = useSessionQuery({ storeSlug });
  const { data: storeData } = useStoreQuery({ storeSlug });

  const overview = sessionData?.overview ?? null;
  const seller = sessionData?.seller;
  // `overview` can legitimately be absent on a session that has one; the STORE
  // query is the honest signal that the answers are in.
  const ready = Boolean(sessionData) && Boolean(storeData);

  // Deep-link to the ACTION, not the page — but SHOW the action rather than
  // performing it. `?guide=add` lands on the list and spotlights its "Add"
  // button; the seller presses it themselves. Opening the create surface for
  // them (the old `?create=1`) skipped the one thing they said they did not
  // know: where the button is. Landing them on an empty list with nothing
  // pointed at is the other failure — this is the middle.
  const categoriesHref = `${getSellerPath(locale, storeSlug, "/categories")}?guide=add`;
  const productsHref = `${getSellerPath(locale, storeSlug, "/products")}?guide=add`;
  // Every item spotlights its own control on arrival, not just the categories
  // and products ones: landing a seller on the Settings page with six cards on
  // it and no indication which one they came for is the same "where is it?"
  // problem the list pages had.
  const paymentHref = `${getSellerPath(locale, storeSlug, "/settings")}?tab=payment&section=payment&guide=payment`;

  const categoriesTitle = t(keys.seller.setupAddCategories);
  const productsTitle = t(keys.seller.setupAddProduct);
  const paymentTitle = t(keys.seller.setupPaymentOptions);
  const logoTitle = t(keys.seller.setupItemLogo);
  const descriptionTitle = t(keys.seller.setupItemDescription);
  const contactTitle = t(keys.seller.setupItemContact);
  const lookTitle = t(keys.seller.setupItemLook);
  const publishTitle = t(keys.seller.setupItemPublish);

  /**
   * Whether the storefront has ever been published.
   *
   * The guide had six rows — categories, products, payment, logo,
   * description, contact — and not one of them was about what the
   * store LOOKS like or whether buyers can see it. A seller could tick every
   * box and still have an unpublished default-looking storefront: the editor
   * was a room nobody was shown.
   *
   * This is the one row that needs data the dashboard did not already hold;
   * the publications list is small and cached for 30s.
   *
   * The predicate itself now lives in `useHasEverPublished`, shared with the
   * editor's first-publish moment so the guide's publish row and the editor can
   * never disagree about whether this store has ever published. A literal move:
   * this file's test is unchanged and still green.
   */
  const canViewDesign =
    seller?.access?.capabilities.design === true &&
    seller.permissions.includes("DESIGN_VIEW");
  const { hasEverPublished: hasPublished } = useHasEverPublished(
    ready && canViewDesign,
  );

  return useMemo<DashboardSetupGuide>(() => {
    const brandingHref = (guide: string) =>
      `${getSellerPath(locale, storeSlug, "/settings")}?tab=branding&section=branding&guide=${guide}`;
    const done = resolveSetupDone({
      hasCategories: overview?.hasCategories === true,
      hasProducts: overview?.hasProducts === true,
      hasPaymentWay: resolveHasPaymentWay({
        hasActivePaymentOptions: overview?.hasActivePaymentOptions,
        acceptsCod:
          seller?.access?.capabilities.manualPayments === false ||
          storeData?.acceptsCod,
      }),
      hasLogo: Boolean(storeData?.logoUrl),
      hasDescription: Boolean(storeData?.description?.trim()),
      hasContact: Boolean(storeData?.whatsappNumber?.trim()),
      hasPublished,
    });
    // Copy and destination per row, keyed by id. The rendered list is then
    // SETUP_ITEM_IDS.map(...) rather than a second hand-written array: a new
    // row cannot reach the guide without joining the list the test pins, and
    // the order on screen is the order declared above.
    const rowCopy: Record<SetupItemId, { title: string; href: string }> = {
      products: { title: productsTitle, href: productsHref },
      categories: { title: categoriesTitle, href: categoriesHref },
      payment: { title: paymentTitle, href: paymentHref },
      logo: { title: logoTitle, href: brandingHref("logo") },
      description: {
        title: descriptionTitle,
        href: brandingHref("description"),
      },
      contact: { title: contactTitle, href: brandingHref("contact") },
      // The store's LOOK: a seller who has products and a way to get paid is
      // exactly who should be asked what their shop looks like. Done when the
      // storefront has been published at least once with a design applied —
      // the same signal the publish row reads, because "I chose a look" and
      // "buyers can see it" are one journey, and a look nobody can see is not
      // finished.
      look: {
        title: lookTitle,
        href: getSellerPath(locale, storeSlug, SETUP_LOOK_PATH),
      },
      publish: {
        title: publishTitle,
        href: getSellerPath(locale, storeSlug, SETUP_PUBLISH_PATH),
      },
    };
    const actionableIds = new Set(actionableSetupIds(seller));
    const coreOnly = seller?.access?.capabilities.profileBranding !== true;
    // The full dashboard retains its store-wide progress facts. RBAC controls
    // which unfinished action this actor can take, not the store's denominator.
    // Core setup counts only its permitted first-product action.
    const visibleIds = coreOnly
      ? [...actionableIds]
      : SETUP_ITEM_IDS.filter(
          (id) =>
            seller?.storeAccess === true &&
            seller.access?.capabilities[SETUP_CAPABILITIES[id]] === true,
        );
    const items: DashboardSetupItem[] = visibleIds.map((id) => ({
      id,
      title: rowCopy[id].title,
      done: done[id],
      href: rowCopy[id].href,
      canAct: actionableIds.has(id),
    }));
    const completed = items.filter((item) => item.done).length;
    return {
      coreOnly,
      items,
      completed,
      total: items.length,
      percent: setupPercent(items.map((item) => item.done)),
      ready,
    };
  }, [
    ready,
    seller,
    lookTitle,
    publishTitle,
    hasPublished,
    locale,
    storeSlug,
    overview?.hasCategories,
    overview?.hasProducts,
    overview?.hasActivePaymentOptions,
    storeData?.acceptsCod,
    storeData?.logoUrl,
    storeData?.description,
    storeData?.whatsappNumber,
    categoriesTitle,
    productsTitle,
    paymentTitle,
    logoTitle,
    descriptionTitle,
    contactTitle,
    categoriesHref,
    productsHref,
    paymentHref,
  ]);
}
